import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { useLoc } from "@/lib/location";
import { Navbar, Footer } from "@/components/site/Navbar";
import { DealCard } from "@/components/site/DealCard";
import { RadiusChips } from "@/components/site/LocationPicker";
import { CategoryIcon, Empty, NativeSelect } from "@/components/site/bits";

const SORTS = { nearest: "Nearest first", expiring: "Expiring soon", new: "Newly published", discount: "Highest discount", popular: "Most popular", claimed: "Most claimed" };

export default function Browse() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const { loc, radius, setRadius } = useLoc();
  const [cats, setCats] = useState([]);
  const [deals, setDeals] = useState(null);
  const [q, setQ] = useState(sp.get("q") || "");
  const sort = sp.get("sort") || "nearest";
  const r = sp.get("radius") ? +sp.get("radius") : radius;
  const category = slug || sp.get("category") || "";
  const cat = cats.find((c) => c.slug === category);

  useEffect(() => {
    api.get("/categories").then((x) => setCats(x.data));
  }, []);
  useEffect(() => {
    setDeals(null);
    api.get("/deals", { params: { lat: loc.lat, lng: loc.lng, radius_km: r, sort, q: sp.get("q") || undefined, category: category || undefined, merchant_id: sp.get("merchant") || undefined, limit: 60 } })
      .then((x) => setDeals(x.data)).catch(() => setDeals([]));
  }, [loc.lat, loc.lng, r, sort, category, sp]);

  const setParam = (k, v) => {
    const n = new URLSearchParams(sp);
    v ? n.set(k, v) : n.delete(k);
    setSp(n);
  };

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-2">
          {cat && <span className="w-11 h-11 rounded-xl bg-red-50 grid place-items-center"><CategoryIcon name={cat.icon} className="w-5 h-5 text-red-500" /></span>}
          <h1 data-testid="browse-title" className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{cat ? cat.name : sp.get("q") ? `Results for “${sp.get("q")}”` : "Deals near you"}</h1>
        </div>
        <p className="text-sm text-slate-500 mb-6">Around <b>{loc.label}</b></p>
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-4 mb-8">
          <div className="flex flex-col md:flex-row gap-3">
            <form onSubmit={(e) => { e.preventDefault(); setParam("q", q); }} className="flex-1 flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 h-10">
              <Search className="w-4 h-4 text-slate-400" /><input data-testid="browse-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search merchants, products, locations" className="flex-1 outline-none text-sm" />
            </form>
            <NativeSelect data-testid="browse-category-select" value={category} disabled={!!slug} onChange={(e) => setParam("category", e.target.value)} className="md:w-56">
              <option value="">All categories</option>
              {cats.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
            </NativeSelect>
            <NativeSelect data-testid="browse-sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)} className="md:w-48">
              {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </NativeSelect>
          </div>
          <RadiusChips value={r} onChange={(v) => { setRadius(v); setParam("radius", v); }} />
        </div>
        {deals === null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">{[0, 1, 2, 3].map((i) => <div key={i} className="h-80 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
        ) : deals.length === 0 ? (
          <Empty testid="browse-empty" title="No live deals match your filters"><p className="text-sm mt-1">Try a larger radius or a different location.</p></Empty>
        ) : (
          <>
            <p data-testid="browse-count" className="text-sm text-slate-500 mb-4">{deals.length} live deal{deals.length > 1 ? "s" : ""} within {r < 1 ? `${r * 1000} m` : `${r} km`}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">{deals.map((d, i) => <DealCard key={d.id} deal={d} index={i} />)}</div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
